/* eslint-disable react/display-name */
import React, { memo } from 'react';
import { utilities } from '@weatherbug/common';
import { ForecastListAdBreak } from './forecastListAdBreak';
import { SolarDivide } from './solarDivide';
import { defaultReading, formatDateStr, hourMinDateFormat, isAdBreak } from './utils';
import { HourCardLarge } from '../hourlyForecast/desktopTabletViews/hourCardLarge';
import { HourCardViewSmall } from '../hourlyForecast/mobileView/hourCardViewSmall';

const { getWindDirection } = utilities;
const defaultIconcode = 999;

type ForecastReadings = number | string;
interface HourlyForecastListItemViewProps {
    forecastPeriodIndex: number;
    isMobile: boolean;
    displayWeekday: boolean;
    dayDisplay: string;
    forecastDateLocalStr: string;
    temperature: ForecastReadings;
    dewPoint: ForecastReadings;
    feelsLike: ForecastReadings; 
    humidity: ForecastReadings;
    windDirectionDegrees: number;
    precipitation: ForecastReadings;
    windSpeed: ForecastReadings;
    iconCode: number;
    description: string;
}

export const formatWindSpeedDisplay = (windspeedDirection: number, windspeed: ForecastReadings): string => {
    let windSpeedDisplay = defaultReading;
    if (windspeedDirection >= 0 && typeof windspeedDirection === 'number') {
        windSpeedDisplay = getWindDirection(windspeedDirection);
    }

    if (typeof windspeed === 'number' && windspeed >= 0 || typeof windspeed === 'string' && parseFloat(windspeed) >= 0) {
        windSpeedDisplay = `${windSpeedDisplay} ${windspeed}`;
    }

    return windSpeedDisplay;
};

export const HourlyForecastListItemView = memo(({ 
    forecastDateLocalStr,
    description,
    temperature = defaultReading,
    dewPoint = defaultReading,
    feelsLike = defaultReading, 
    humidity = defaultReading,
    windDirectionDegrees,
    precipitation = defaultReading,
    windSpeed = defaultReading,
    iconCode = defaultIconcode,
    dayDisplay,
    displayWeekday,
    isMobile, 
    forecastPeriodIndex }: HourlyForecastListItemViewProps) => {
        
    let humidityDisplay;
    if (!Number.isNaN(humidity)) {
        humidityDisplay = humidity as number >= 0 ? `${humidity}%` : humidity;
    }
    let precipDisplay;
    if (!Number.isNaN(precipitation)) {
        precipDisplay = precipitation as number >= 0 ? `${precipitation}%` : precipitation;
    }
    const windDisplay = formatWindSpeedDisplay(windDirectionDegrees, windSpeed);

    const shortHourTimeDisplay = forecastDateLocalStr ? formatDateStr(forecastDateLocalStr, hourMinDateFormat)?.toLowerCase() : defaultReading;

    const currIndexIsAdBreak = isAdBreak(forecastPeriodIndex, isMobile);

    return (
        <>
            {
                <SolarDivide 
                    forecastDateLocalStr={forecastDateLocalStr} 
                    hourCardIndex={forecastPeriodIndex} 
                    key={`hourly_solar_div#${forecastPeriodIndex}`} />
            }
            {   isMobile 
                ? <HourCardViewSmall 
                    key={`hourly-card_view_ small#${forecastPeriodIndex}`}
                    cardIndex={forecastPeriodIndex}
                    day={dayDisplay}
                    dewPoint={dewPoint as string}
                    temperature={temperature as string}
                    hour={shortHourTimeDisplay as string}
                    description={description}
                    humidity={humidityDisplay as string}
                    wind={windDisplay}
                    precipitation={precipDisplay as string}
                    iconCode={iconCode}
                    feelsLike={feelsLike as string}
                    displayWeekday={displayWeekday} />
                : <HourCardLarge
                    key={`hourly-card_large#${forecastPeriodIndex}`}
                    cardLocalTime={forecastDateLocalStr}
                    day={dayDisplay}
                    cardIndex={forecastPeriodIndex}
                    dewPoint={dewPoint as string}
                    temperature={temperature as string}
                    hour={shortHourTimeDisplay as string}
                    description={description}
                    humidity={humidityDisplay as string}
                    wind={windDisplay}
                    feelsLike={feelsLike}
                    iconCode={iconCode} />
            }
            {
                currIndexIsAdBreak
                    && <ForecastListAdBreak 
                        adIndex={forecastPeriodIndex} 
                        key={`for_hourly--adbreak#${forecastPeriodIndex}`} />
            }        
        </>
    );
});